import {
  FULFILLMENT_SOURCES,
  type FulfillmentSource,
} from '../../../lib/fulfillment-source'

export const ADMIN_CATALOG_PATH = '/admin/catalog/products'

const CATALOG_SCROLL_KEY_PREFIX = 'pexify:admin-catalog-scroll:'

const SEGMENTS = [
  'all',
  'active',
  'draft',
  'archived',
  'missing',
  'personalizable',
  'mto',
] as const

const PAGE_SIZES = [50, 100, 250, 'all'] as const

export type SegmentId = (typeof SEGMENTS)[number]
export type PageSize = (typeof PAGE_SIZES)[number]

export type AdminCatalogSearch = {
  segment?: SegmentId
  q?: string
  source?: FulfillmentSource | 'all'
  category?: string
  classification?: string
  producer?: string
  tags?: string[]
  group?: 'category'
  hidden?: 'show'
  code_mode?: 'include' | 'exclude'
  code?: string
  page?: number
  page_size?: PageSize
}

export type ProductDetailSearch = { returnTo?: string }

export type CatalogDirectoryState = {
  segment: SegmentId
  search: string
  sourceType: FulfillmentSource | 'all'
  categoryId: string
  classification: string
  producerId: string
  selectedTags: string[]
  groupByCategory: boolean
  showHiddenCategories: boolean
  codeFilter: {
    mode: 'include' | 'exclude'
    value: string
  }
  page: number
  pageSize: PageSize
}

type CatalogScrollSnapshot = {
  catalogUrl: string
  scrollY: number
}

function trimmedString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function isOneOf<T extends string | number>(
  value: unknown,
  allowed: readonly T[],
): value is T {
  return allowed.some((candidate) => candidate === value)
}

function normalizePage(value: unknown): number | undefined {
  let parsed: number

  if (typeof value === 'number') {
    parsed = value
  } else if (typeof value === 'string' && /^\d+$/.test(value)) {
    parsed = Number(value)
  } else {
    return undefined
  }

  if (!Number.isSafeInteger(parsed) || parsed <= 1) return undefined
  return parsed
}

function normalizePageSize(value: unknown): PageSize | undefined {
  if (value === 'all') return 'all'
  const parsed =
    typeof value === 'string' && /^(50|100|250)$/.test(value) ? Number(value) : value

  if (!isOneOf(parsed, PAGE_SIZES) || parsed === 50) return undefined
  return parsed
}

function normalizeTags(value: unknown): string[] | undefined {
  const rawTags = Array.isArray(value) ? value : typeof value === 'string' ? [value] : []
  const firstByNormalizedTag = new Map<string, string>()

  for (const rawTag of rawTags) {
    const tag = trimmedString(rawTag)
    if (!tag) continue

    const normalizedTag = tag.toLowerCase()
    if (!firstByNormalizedTag.has(normalizedTag)) {
      firstByNormalizedTag.set(normalizedTag, tag)
    }
  }

  const tags = [...firstByNormalizedTag.entries()]
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
    .map(([, displayTag]) => displayTag)

  return tags.length > 0 ? tags : undefined
}

function nonDefaultString(value: unknown): string | undefined {
  const normalized = trimmedString(value)
  return normalized && normalized !== 'all' ? normalized : undefined
}

function hasUnsafeReturnCharacters(value: string): boolean {
  return (
    value.includes('\\') ||
    [...value].some((character) => {
      const codePoint = character.codePointAt(0) ?? 0
      return codePoint < 32 || codePoint === 127
    })
  )
}

export function normalizeCatalogSearch(raw: Record<string, unknown>): AdminCatalogSearch {
  const normalized: AdminCatalogSearch = {}

  if (isOneOf(raw.segment, SEGMENTS) && raw.segment !== 'all') {
    normalized.segment = raw.segment
  }

  const query = trimmedString(raw.q)
  if (query) normalized.q = query

  if (isOneOf(raw.source, FULFILLMENT_SOURCES)) {
    normalized.source = raw.source
  }

  const category = nonDefaultString(raw.category)
  if (category) normalized.category = category

  const classification = nonDefaultString(raw.classification)
  if (classification) normalized.classification = classification

  const producer = nonDefaultString(raw.producer)
  if (producer) normalized.producer = producer

  const tags = normalizeTags(raw.tags)
  if (tags) normalized.tags = tags

  if (raw.group === 'category') normalized.group = 'category'
  if (raw.hidden === 'show') normalized.hidden = 'show'

  const code = trimmedString(raw.code)
  if (code) {
    normalized.code = code
    if (raw.code_mode === 'include') normalized.code_mode = 'include'
  }

  const page = normalizePage(raw.page)
  if (page) normalized.page = page

  const pageSize = normalizePageSize(raw.page_size)
  if (pageSize) normalized.page_size = pageSize

  return normalized
}

export function catalogSearchToState(search: AdminCatalogSearch): CatalogDirectoryState {
  const normalized = normalizeCatalogSearch(search as Record<string, unknown>)

  return {
    segment: normalized.segment ?? 'all',
    search: normalized.q ?? '',
    sourceType: normalized.source ?? 'all',
    categoryId: normalized.category ?? 'all',
    classification: normalized.classification ?? 'all',
    producerId: normalized.producer ?? 'all',
    selectedTags: normalized.tags ?? [],
    groupByCategory: normalized.group === 'category',
    showHiddenCategories: normalized.hidden === 'show',
    codeFilter: {
      mode: normalized.code_mode ?? 'exclude',
      value: normalized.code ?? '',
    },
    page: normalized.page ?? 1,
    pageSize: normalized.page_size ?? 50,
  }
}

export function stateToCatalogSearch(state: CatalogDirectoryState): AdminCatalogSearch {
  return normalizeCatalogSearch({
    segment: state.segment,
    q: state.search,
    source: state.sourceType,
    category: state.categoryId,
    classification: state.classification,
    producer: state.producerId,
    tags: state.selectedTags,
    group: state.groupByCategory ? 'category' : undefined,
    hidden: state.showHiddenCategories ? 'show' : undefined,
    code_mode: state.codeFilter.mode,
    code: state.codeFilter.value,
    page: state.page,
    page_size: state.pageSize,
  })
}

function parseCatalogReturnTo(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0 || value !== value.trim()) return null
  if (!value.startsWith('/') || value.startsWith('//')) return null
  if (hasUnsafeReturnCharacters(value)) return null

  try {
    const decoded = decodeURI(value)
    if (hasUnsafeReturnCharacters(decoded)) return null

    const parsed = new URL(value, 'https://pexify.invalid')
    if (parsed.origin !== 'https://pexify.invalid') return null
    if (parsed.pathname !== ADMIN_CATALOG_PATH) return null
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return null
  }
}

export function normalizeProductDetailSearch(
  raw: Record<string, unknown>,
): ProductDetailSearch {
  const returnTo = parseCatalogReturnTo(raw.returnTo)
  return returnTo ? { returnTo } : {}
}

export function buildCatalogReturnTo(search: AdminCatalogSearch): string {
  const normalized = normalizeCatalogSearch(search as Record<string, unknown>)
  const params = new URLSearchParams()

  if (normalized.segment) params.set('segment', normalized.segment)
  if (normalized.q) params.set('q', normalized.q)
  if (normalized.source) params.set('source', normalized.source)
  if (normalized.category) params.set('category', normalized.category)
  if (normalized.classification) params.set('classification', normalized.classification)
  if (normalized.producer) params.set('producer', normalized.producer)
  for (const tag of normalized.tags ?? []) params.append('tags', tag)
  if (normalized.group) params.set('group', normalized.group)
  if (normalized.hidden) params.set('hidden', normalized.hidden)
  if (normalized.code_mode) params.set('code_mode', normalized.code_mode)
  if (normalized.code) params.set('code', normalized.code)
  if (normalized.page) params.set('page', String(normalized.page))
  if (normalized.page_size) params.set('page_size', String(normalized.page_size))

  const query = params.toString()
  return query ? `${ADMIN_CATALOG_PATH}?${query}` : ADMIN_CATALOG_PATH
}

export function resolveCatalogReturnTo(value: unknown): string {
  return parseCatalogReturnTo(value) ?? ADMIN_CATALOG_PATH
}

function catalogScrollKey(catalogUrl: string): string | null {
  const normalizedUrl = parseCatalogReturnTo(catalogUrl)
  return normalizedUrl ? `${CATALOG_SCROLL_KEY_PREFIX}${normalizedUrl}` : null
}

export function saveCatalogScroll(
  storage: Storage,
  catalogUrl: string,
  scrollY: number,
): void {
  const key = catalogScrollKey(catalogUrl)
  if (!key || !Number.isFinite(scrollY) || scrollY < 0) return

  const snapshot: CatalogScrollSnapshot = { catalogUrl, scrollY }
  try {
    storage.setItem(key, JSON.stringify(snapshot))
  } catch {
    return
  }
}

export function consumeCatalogScroll(
  storage: Storage,
  catalogUrl: string,
): number | null {
  const key = catalogScrollKey(catalogUrl)
  if (!key) return null

  try {
    const stored = storage.getItem(key)
    if (stored === null) return null

    let snapshot: unknown
    try {
      snapshot = JSON.parse(stored)
    } catch {
      storage.removeItem(key)
      return null
    }

    storage.removeItem(key)
    if (!snapshot || typeof snapshot !== 'object') return null

    const candidate = snapshot as Partial<CatalogScrollSnapshot>
    if (
      candidate.catalogUrl !== catalogUrl ||
      typeof candidate.scrollY !== 'number' ||
      !Number.isFinite(candidate.scrollY) ||
      candidate.scrollY < 0
    ) {
      return null
    }

    return candidate.scrollY
  } catch {
    return null
  }
}
